import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import {
  ASSIGNEE_FILTER_ATTRIBUTE,
  AUTHOR_FILTER_ATTRIBUTE,
  DUE_DATE_FILTER_ATTRIBUTE,
  PUBLISHED_FILTER_ATTRIBUTE,
  SEARCH_FILTER_ATTRIBUTE,
  STATUS_FILTER_ATTRIBUTE,
  SPACE_FILTER_ATTRIBUTE,
  TYPE_FILTER_ATTRIBUTE,
  CLAIM_FILTER_ATTRIBUTE,
  USER_STATUS_FILTER_ATTRIBUTE,
  WITH_RESOLVED_FILTER_ATTRIBUTE,
} from '@/config/search_filter/task_filter'
import type { LaravelCollection, LaravelPaginated, LaravelResource } from './types'
import type { Task, TaskFile } from '@/models/Task'
import type { Contact } from '@/models/Contact'
import type { Protocol } from '@/models/Protocol'
import type { TaskListRequest } from '@/models/Filters'

function buildTaskListParams(data: TaskListRequest): string {
  const params: string[] = []
  const { listQuery, filterAttributes } = data
  if (listQuery) {
    if (listQuery.page) params.push('page=' + listQuery.page)
    if (listQuery.limit) params.push('limit=' + listQuery.limit)
    if (listQuery.order) params.push('order=' + listQuery.order)
  }
  if (filterAttributes) {
    if (filterAttributes.due_date?.length)
      params.push(DUE_DATE_FILTER_ATTRIBUTE + '=' + filterAttributes.due_date.join(','))
    if (filterAttributes.author?.length)
      params.push(AUTHOR_FILTER_ATTRIBUTE + '=' + filterAttributes.author.join(','))
    if (filterAttributes.published?.length)
      params.push(PUBLISHED_FILTER_ATTRIBUTE + '=' + filterAttributes.published.join(','))
    if (filterAttributes.status)
      params.push(STATUS_FILTER_ATTRIBUTE + '=' + filterAttributes.status)
    if (filterAttributes.assignees?.length)
      params.push(ASSIGNEE_FILTER_ATTRIBUTE + '=' + filterAttributes.assignees.join(','))
    if (filterAttributes.teams?.length)
      params.push(SPACE_FILTER_ATTRIBUTE + '=' + filterAttributes.teams.join(','))
    // Titles are free text: encoded, unlike the ids around them.
    if (filterAttributes.types?.length)
      params.push(TYPE_FILTER_ATTRIBUTE + '=' + encodeURIComponent(filterAttributes.types.join(',')))
    if (filterAttributes.is_claim)
      params.push(CLAIM_FILTER_ATTRIBUTE + '=true')
    if (filterAttributes.search)
      params.push(SEARCH_FILTER_ATTRIBUTE + '=' + filterAttributes.search)
    if (filterAttributes.user_status?.length)
      params.push(USER_STATUS_FILTER_ATTRIBUTE + '=' + filterAttributes.user_status)
    if (
      filterAttributes.hide_resolved !== null &&
      filterAttributes.hide_resolved !== undefined &&
      (!filterAttributes.status || (filterAttributes.user_status && filterAttributes.user_status.length))
    ) {
      params.push(WITH_RESOLVED_FILTER_ATTRIBUTE + '=' + filterAttributes.hide_resolved)
    }
  }
  return params.join('&')
}

/** GET /api/v1/tasks — paginated, with filters */
export function getAllTasks(
  data: TaskListRequest,
): Promise<AxiosResponse<LaravelPaginated<Task>>> {
  return request({
    url: '/api/v1/tasks?' + buildTaskListParams(data),
    method: 'get',
  })
}

/** GET /api/v1/tasks/print — PDF blob of filtered list */
export function printTasks(data: TaskListRequest): Promise<AxiosResponse<Blob>> {
  return request({
    url: '/api/v1/tasks/print?' + buildTaskListParams(data),
    method: 'get',
    responseType: 'blob',
  })
}

/** POST /api/v1/tasks/{task_id}/contacts */
export function linkContactToTask(
  task_id: string,
  contact_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/contacts',
    method: 'post',
    data: { contact_id },
  })
}

/**
 * DELETE /api/v1/tasks/{task_id}/contacts/{contact_id}
 *
 * @param forgetMapping when true, also drops the learned sender->contact mapping so future mail
 *   from this sender no longer auto-links this customer.
 */
export function removeContactToTaskLink(
  task_id: string,
  contact_id: string,
  forgetMapping?: boolean,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/contacts/' + contact_id,
    method: 'delete',
    params: forgetMapping ? { forget_mapping: 1 } : undefined,
  })
}

/** GET /api/v1/tasks/{task_id}/contact-suggestions — candidate contact ids for the sender. */
export function getContactSuggestions(
  task_id: string,
): Promise<AxiosResponse<{ contact_ids: string[] }>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/contact-suggestions',
    method: 'get',
  })
}

/** POST /api/v1/tasks/{task_id}/email-participants — add an external recipient. */
export function addEmailParticipant(
  task_id: string,
  address: string,
  name?: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/email-participants',
    method: 'post',
    data: name ? { address, name } : { address },
  })
}

/** DELETE /api/v1/tasks/{task_id}/email-participants/{address} */
export function removeEmailParticipant(
  task_id: string,
  address: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/email-participants/' + encodeURIComponent(address),
    method: 'delete',
  })
}

/** PUT /api/v1/tasks/{task_id}/contacts/{contact_id} — toggle public */
export function changeContactPublic(
  task_id: string,
  contact_id: string,
  status: boolean,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/contacts/' + contact_id,
    method: 'put',
    data: { public: status },
  })
}

/** GET /api/v1/tasks?filter[search]= — backend matches title and prefix-number */
export function searchTaskByString(
  search: string = '',
): Promise<AxiosResponse<LaravelCollection<Task>>> {
  return request({
    url: '/api/v1/tasks?filter[search]=' + encodeURIComponent(search) + '&limit=10',
    method: 'get',
  })
}

/** POST /api/v1/tasks/{task_id}/files */
export function uploadFile(
  task_id: string,
  file: string,
): Promise<AxiosResponse<LaravelResource<TaskFile>>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/files',
    method: 'post',
    data: { file },
  })
}

interface UploadedFile {
  id: string
  original_name: string
  url: string
}

/** POST /api/v1/tasks/{task_id}/files (multipart) – inline editor image upload */
export function uploadInlineImage(
  task_id: string,
  file: File,
): Promise<AxiosResponse<LaravelResource<UploadedFile>>> {
  const data = new FormData()
  data.append('file', file)
  data.append('name', file.name)
  return request({
    url: '/api/v1/tasks/' + task_id + '/files',
    method: 'post',
    data,
  })
}

/** GET /api/v1/tasks/{task_id}/files */
export function getFilesForTask(
  task_id: string,
): Promise<AxiosResponse<LaravelCollection<TaskFile>>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/files',
    method: 'get',
  })
}

/** DELETE /api/v1/tasks/{task_id}/files/{file_id} */
export function deleteFileForTask(
  task_id: string,
  file_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/files/' + file_id,
    method: 'delete',
  })
}

/**
 * POST /api/v1/tasks/{task_id}/protocols — link protocol to task.
 *
 * @param source "manual" (default, omitted) for an explicit link via
 *   the UI, "mention" for a soft link auto-created from a #-mention
 *   in the body. Backend persists this so the two can be distinguished.
 */
export function linkProtocolToTask(
  task_id: string,
  protocol_id: string,
  source?: 'manual' | 'mention',
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/protocols',
    method: 'post',
    data: source ? { protocol_id, source } : { protocol_id },
  })
}

/** DELETE /api/v1/tasks/{task_id}/protocols/{protocol_id} */
export function removeProtocolToTaskLink(
  task_id: string,
  protocol_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + task_id + '/protocols/' + protocol_id,
    method: 'delete',
  })
}

interface CreateTaskFormData {
  id?: string
  published: boolean
  author_user_id?: string
  assignees?: Array<{ user_id: string; status: string }>
  viewers?: string[]
  due_date?: number | null
  title?: string
  body?: string
  type?: string
  space_id: string
  claims?: unknown[]
  priority?: number | string
  comments_enabled?: boolean
  linked_contacts?: string[]
  linked_protocols?: string[]
}

/** POST /api/v1/tasks */
export function createTask(
  formData: CreateTaskFormData,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  const data = {
    id: formData.id,
    published: formData.published,
    author_user_id: formData.author_user_id,
    assignees: formData.assignees,
    viewers: formData.viewers,
    due_date: formData.due_date,
    title: formData.title,
    body: formData.body,
    type: formData.type,
    space_id: formData.space_id,
    claims: formData.claims,
    priority: formData.priority,
    comments_enabled: formData.comments_enabled,
    linked_contacts: formData.linked_contacts,
    linked_protocols: formData.linked_protocols,
  }
  cleanup(data as Record<string, unknown>)
  return request({
    url: '/api/v1/tasks/',
    method: 'post',
    data,
  })
}

/** GET /api/v1/tasks/{id} */
export function getTaskById(id: string): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + id,
    method: 'get',
  })
}

/** GET /api/v1/tasks/{id}/print — PDF blob */
export function printTaskById(id: string): Promise<AxiosResponse<Blob>> {
  return request({
    url: '/api/v1/tasks/' + id + '/print',
    method: 'get',
    responseType: 'blob',
  })
}

/** DELETE /api/v1/tasks/{id} */
export function deleteTask(id: string): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + id,
    method: 'delete',
  })
}

/** GET /api/v1/tasks/{id}/contacts */
export function getContactsForTask(
  id: string,
): Promise<AxiosResponse<LaravelCollection<Contact>>> {
  return request({
    url: '/api/v1/tasks/' + id + '/contacts',
    method: 'get',
  })
}

/** GET /api/v1/tasks/{id}/protocols */
export function getProtocolsForTask(
  id: string,
): Promise<AxiosResponse<LaravelCollection<Protocol>>> {
  return request({
    url: '/api/v1/tasks/' + id + '/protocols',
    method: 'get',
  })
}

/** PATCH /api/v1/tasks/{id}/status — set assignee status (open/in_progress/closed).
 *  Pass user_id to change another assignee's status (requires task edit permission). */
export function setAssigneeStatusForTask(
  id: string,
  status: string,
  user_id?: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + id + '/status',
    method: 'patch',
    data: user_id ? { status, user_id } : { status },
  })
}

/** PATCH /api/v1/tasks/{id} */
export function updateTask(
  task: Partial<Task> & { id: string },
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + task.id,
    method: 'patch',
    data: task,
  })
}

/** PATCH /api/v1/tasks/{id}/watch */
export function setWatchStatusForTask(
  id: string,
  status: 'watch' | 'unwatch',
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + id + '/watch',
    method: 'patch',
    data: { status },
  })
}

/** DELETE /api/v1/tasks/{id}/assignees/{user_id} */
export function unassignUserFromTask(
  id: string,
  user_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + id + '/assignees/' + user_id,
    method: 'delete',
  })
}

// Comments live on the V2 API — see upsertComment / deleteComment / sendCommentToCustomer in
// api/v2/tasks.ts.

/**
 * POST /api/v1/tasks/{id}/viewers
 *
 * @param source "manual" (default) for an explicit add via the UI,
 *   "mention" for a soft add from an @-mention in the body.
 */
export function addViewer(
  id: string,
  user_id: string,
  source?: 'manual' | 'mention',
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v1/tasks/' + id + '/viewers',
    method: 'post',
    data: source ? { user_id, source } : { user_id },
  })
}

/** DELETE /api/v1/tasks/{id}/viewers/{user_id} */
export function removeViewer(
  id: string,
  user_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/tasks/' + id + '/viewers/' + user_id,
    method: 'delete',
  })
}

/** Delete null/undefined/empty properties from an object */
function cleanup(obj: Record<string, unknown>, recurse?: boolean): void {
  for (const i in obj) {
    if (obj[i] == null || obj[i] === '') {
      delete obj[i]
    } else if (recurse && typeof obj[i] === 'object' && obj[i] !== null) {
      cleanup(obj[i] as Record<string, unknown>, recurse)
    }
  }
}
